import {
  Col,
  AlignItems,
  Box,
  Display,
  FlexDirection,
  Heading1,
  Image,
  JustifyContent,
  Grid,
} from '@gousto-internal/citrus-react'
import { datadogRum } from '@datadog/browser-rum'
import React, { useEffect, useRef, useState } from 'react'
import { browserHistory } from 'react-router'

import { useDashboardExperience } from './hooks/useDashboardExperience'
import {
  DashboardLegacyActions,
  DashboardSection,
  dashboardActionRoutes,
} from './components/dashboard-experience'
import { Notification } from './components/Notification'
import { OrderTabs, OrderTile } from './components/OrderTile/index'
import { OrderRecommendations } from './components/OrderRecommendations/OrderRecommendations'
import { useDashboardActionTracking } from './hooks/useDashboardActionTracking'
import { useOrderTracking } from './hooks/useOrderTracking'

// eslint-disable-next-line @typescript-eslint/no-require-imports
const getLoadingGif = () => require('./assets/Loading-Icon.gif')

const getDefaultActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
) => {
  const defaultActiveIndex = orderTabs.findIndex((tab) => tab.default_active)

  return defaultActiveIndex === -1 ? 0 : defaultActiveIndex
}

const getActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
  activeOrderId: string | undefined,
) => {
  const activeTabIndexFromOrderId = orderTabs.findIndex((tab) => tab.order_id === activeOrderId)

  return activeTabIndexFromOrderId === -1
    ? getDefaultActiveTabIndex(orderTabs)
    : activeTabIndexFromOrderId
}

type MyHomePageProps = {
  onSessionExpired?: () => void
}

const MyHomePageLoadingView = () => (
  <main data-testid="my-home-page-main">
    <Box
      aria-label="Loading My Home Page"
      data-testid="my-home-page-loading"
      role="status"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      minHeight="350px"
      width="100%"
    >
      <Image src={getLoadingGif()} width="150" alt="" />
    </Box>
  </main>
)

export function MyHomePage({ onSessionExpired }: MyHomePageProps = {}) {
  const {
    page_title,
    order_tabs,
    error,
    isLoading,
    orders = [],
    actions = [],
    downloadApp,
    rafCampaign,
    rafStatus,
    sections,
    isSessionExpired,
  } = useDashboardExperience()
  const { trackDashboardActionClick } = useDashboardActionTracking()
  const { trackHomeOrdersTabClick } = useOrderTracking()
  const [activeOrderId, setActiveOrderId] = useState<string>()
  const [pendingSelectedDeliveryDate, setPendingSelectedDeliveryDate] = useState<string>()
  const [displayedOrderTabs, setDisplayedOrderTabs] = useState(order_tabs)
  const [displayedOrders, setDisplayedOrders] = useState(orders)
  const hasHandledExpiredSession = useRef(false)
  const hasOrderMismatch = displayedOrders.length !== displayedOrderTabs.length

  const activeTabIndex = getActiveTabIndex(displayedOrderTabs, activeOrderId)

  const activeOrder = displayedOrders.find((order) => order.id === activeOrderId)

  useEffect(() => {
    if (!isSessionExpired || hasHandledExpiredSession.current) {
      return
    }

    hasHandledExpiredSession.current = true
    onSessionExpired?.()
  }, [isSessionExpired, onSessionExpired])

  useEffect(() => {
    setDisplayedOrderTabs(order_tabs)
  }, [order_tabs])

  useEffect(() => {
    setDisplayedOrders(orders)
  }, [orders])

  useEffect(() => {
    if (!displayedOrderTabs.length) {
      return
    }

    if (pendingSelectedDeliveryDate) {
      const matchingOrder = displayedOrders.find(
        (order) => order.analytics.delivery_date === pendingSelectedDeliveryDate,
      )

      if (matchingOrder) {
        setActiveOrderId(matchingOrder.id)
        setPendingSelectedDeliveryDate(undefined)
        return
      }
    }

    if (activeOrderId && displayedOrderTabs.some((tab) => tab.order_id === activeOrderId)) {
      return
    }

    const defaultActiveTab = displayedOrderTabs[getDefaultActiveTabIndex(displayedOrderTabs)]
    setActiveOrderId(defaultActiveTab.order_id)
  }, [displayedOrderTabs, displayedOrders, activeOrderId, pendingSelectedDeliveryDate])

  const handleOrderTabChange = (index: number) => {
    const selectedTab = displayedOrderTabs[index]

    setActiveOrderId(selectedTab.order_id)

    const order = displayedOrders.find((currentOrder) => currentOrder.id === selectedTab.order_id)

    if (order) {
      trackHomeOrdersTabClick({
        order,
        tabPosition: index + 1,
      })
    }
  }

  const handleDashboardActionClick = (actionType: string) => {
    trackDashboardActionClick(actionType)

    const route = dashboardActionRoutes[actionType]

    if (route) {
      browserHistory.push(route)
    }
  }

  const handleOrderSkipSuccess = (orderId: string) => {
    setDisplayedOrders((currentOrders) =>
      currentOrders.map((currentOrder) =>
        currentOrder.id === orderId
          ? {
              ...currentOrder,
              help_cta: null,
              restore_cta: currentOrder.restore_cta ?? 'Restore order',
              state: 'skip',
              state_title: 'Skipped',
              price: {
                ...currentOrder.price,
                amount: null,
                original: null,
                kind: null,
              },
            }
          : currentOrder,
      ),
    )
    setDisplayedOrderTabs((currentTabs) =>
      currentTabs.map((currentTab) =>
        currentTab.order_id === orderId
          ? {
              ...currentTab,
              discount: null,
              icon: 'cancelled',
            }
          : currentTab,
      ),
    )
  }

  useEffect(() => {
    if (hasOrderMismatch) {
      datadogRum.addAction('dashboard_experience_order_mismatch', {
        page: 'MyHomePage',
        selectedIndex: activeTabIndex,
        orderTabsLength: displayedOrderTabs.length,
        ordersLength: displayedOrders.length,
      })
    }
  }, [hasOrderMismatch, activeTabIndex, displayedOrderTabs.length, displayedOrders.length])

  if (isLoading) {
    return <MyHomePageLoadingView />
  }

  return (
    <main data-testid="my-home-page-main">
      <Box
        maxWidth="1200px"
        style={{ margin: '0 auto' }}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.FlexStart}
        gap={[4, 6]}
        paddingV={[4, 6]}
        paddingH={[3, 0]}
      >
        {sections ? (
          <DashboardSection
            items={sections.header}
            label="dashboard header"
            onActionClick={handleDashboardActionClick}
          />
        ) : (
          <Box
            width="100%"
            minHeight={['52px', '40px']}
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.FlexStart}
            paddingV={[3, 1]}
            paddingH={[3, 0]}
            data-testid="my-home-page-title-box"
          >
            {error ? (
              <Notification
                state="error"
                message="Oops something went wrong. Please try again later."
              />
            ) : (
              <Heading1>{page_title}</Heading1>
            )}
          </Box>
        )}

        <Grid gap={[4, 3]} width={'100%'}>
          <Col size={[12, null, null, 2]} display={[Display.None, null, null, Display.Block]}>
            <Box>
              <section aria-label="order-tabs">
                <Box>
                  <OrderTabs
                    orderTabs={displayedOrderTabs}
                    activeIndex={activeTabIndex}
                    onChange={handleOrderTabChange}
                  />
                </Box>
              </section>
            </Box>
          </Col>
          <Col size={[12, null, null, 10]}>
            <Box
              width="100%"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={[4, 3]}
            >
              <section aria-label="Right column">
                {!hasOrderMismatch && activeOrder ? (
                  <Box width="100%" display={[Display.Flex, null, null, Display.None]}>
                    <section
                      aria-label="order-tabs-mobile"
                      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                    >
                      <OrderTabs
                        orderTabs={displayedOrderTabs}
                        activeIndex={activeTabIndex}
                        onChange={handleOrderTabChange}
                        variant="mobile"
                      />
                    </section>
                  </Box>
                ) : null}
                {hasOrderMismatch ? (
                  <Notification
                    state="error"
                    message="Oops something went wrong. Please try again later."
                  />
                ) : activeOrder ? (
                  <Box
                    width="100%"
                    display={Display.Flex}
                    flexDirection={FlexDirection.Column}
                    gap={[4, 4]}
                  >
                    <OrderTile
                      onDeliveryDateSaved={(selectedDeliveryDate) =>
                        setPendingSelectedDeliveryDate(selectedDeliveryDate)
                      }
                      onSkipSuccess={handleOrderSkipSuccess}
                      order={activeOrder}
                    />
                    <OrderRecommendations
                      additions={activeOrder.additions}
                      newAndLimited={activeOrder.new_and_limited}
                      orderId={activeOrder.id}
                      topPicks={activeOrder.top_picks}
                    />
                  </Box>
                ) : null}
              </section>

              {sections ? (
                <>
                  <DashboardSection
                    items={sections.global_orders_additions}
                    label="dashboard global order additions"
                    onActionClick={handleDashboardActionClick}
                  />
                  <DashboardSection
                    items={sections.footer}
                    label="dashboard footer"
                    onActionClick={handleDashboardActionClick}
                  />
                </>
              ) : (
                <DashboardLegacyActions
                  actions={actions}
                  downloadApp={downloadApp}
                  rafCampaign={rafCampaign}
                  rafStatus={rafStatus}
                  onActionClick={handleDashboardActionClick}
                />
              )}
            </Box>
          </Col>
        </Grid>
      </Box>
    </main>
  )
}
